import { listRooms, resultSchema, type Mutation, type StoredRoom } from './content';
import type { Connection } from './network';
import type { AuthClient } from './auth';

interface SavedRequest { path: string; body: unknown }
function objectWrite(request: SavedRequest | null): boolean {
  return !!request && typeof request.body === 'object' && request.body !== null && 'kind' in request.body && String(request.body.kind).startsWith('object.');
}
function element<T extends HTMLElement>(id: string): T { const value = document.getElementById(id); if (!value) throw new Error(`Missing ${id}`); return value as T; }

/** Room management and placement; interactive transforms live in BuildingPanel. */
export class RoomPanel {
  private rooms = element<HTMLSelectElement>('room-select');
  private name = element<HTMLInputElement>('room-name');
  private objects = element<HTMLSelectElement>('object-select');
  private roomList: StoredRoom[] = [];
  private signature = '';
  private busy = false;
  private pending: SavedRequest | null = null;
  private storageKey = '';
  private generation = 0;
  constructor(private connection: Connection, private enter: (id: string) => void, private auth: AuthClient) {
    element('enter-room').addEventListener('click', () => { if (this.rooms.value) this.enter(this.rooms.value); });
    element('create-room').addEventListener('click', () => {
      void this.write({ path: '/api/rooms', body: { requestId: crypto.randomUUID(), id: `room_${crypto.randomUUID()}`, settings: { name: this.name.value.trim() || 'New room', spawn: { x: 0, y: 0, z: 4 } } } });
    });
    element('rename-room').addEventListener('click', () => {
      const room = this.connection.state.content?.room; if (!room) return;
      void this.mutate({ requestId: crypto.randomUUID(), kind: 'room.update', expectedRevision: room.revision, settings: { ...room.settings, name: this.name.value.trim() || room.settings.name } });
    });
    element('add-fixture').addEventListener('click', () => {
      const self = this.connection.state.entities.get(this.connection.state.selfId); if (!self) return;
      void this.mutate({ requestId: crypto.randomUUID(), kind: 'object.create', expectedRevision: 0, objectId: crypto.randomUUID(), object: { name: 'Fixture cube', assetId: 'builtin_cube_v1', position: { x: Math.max(-10, Math.min(10, self.position.x + 2)), y: .5, z: Math.max(-10, Math.min(10, self.position.z)) }, rotation: [0, 0, 0, 1], scale: { x: 1, y: 1, z: 1 }, color: '#69b8f7' } });
    });
    for (const id of ['retry-write', 'retry-object-write']) element(id).addEventListener('click', () => { if (this.pending) void this.write(this.pending); });
    this.render();
  }
  authChanged(): void {
    this.generation++; this.busy = false; this.pending = null; this.signature = ''; this.roomList = []; this.rooms.replaceChildren(); this.objects.replaceChildren();
    element('save-status').textContent = ''; element('object-save-status').textContent = '';
    const state = this.auth.state;
    this.storageKey = state?.user ? `offworld.pending-write.v2.${state.worldId}.${state.user.id}` : '';
    if (this.storageKey) {
      try { const saved = sessionStorage.getItem(this.storageKey); if (saved) { const value = JSON.parse(saved) as SavedRequest; if (/^\/api\/rooms(?:\/[a-zA-Z0-9_-]{1,64}\/commands)?$/.test(value.path)) this.pending = value; } } catch { /* Optional retry storage. */ }
      void this.refreshRooms();
    }
    this.render();
  }
  private selected() { return this.connection.state.content?.objects.find(o => o.id === this.objects.value); }
  refreshAccess(): void { if (this.auth.state?.user) void this.refreshRooms(); this.render(); }
  private async refreshRooms(): Promise<void> {
    const generation = this.generation;
    try {
      const rooms = await listRooms(); if (generation !== this.generation) return;
      this.roomList = rooms; const selected = this.rooms.value || this.connection.roomID;
      this.rooms.replaceChildren(...this.roomList.map(room => new Option(room.settings.name, room.id)));
      this.rooms.value = this.roomList.some(room => room.id === selected) ? selected : 'lobby';
    } catch (error) { if (generation === this.generation) element('save-status').textContent = error instanceof Error ? error.message : 'Rooms unavailable'; }
  }
  private mutate(body: Mutation): Promise<void> { return this.write({ path: `/api/rooms/${this.connection.roomID}/commands`, body }); }
  private retain(value: SavedRequest | null): void {
    this.pending = value;
    try { if (this.storageKey) { if (value) sessionStorage.setItem(this.storageKey, JSON.stringify(value)); else sessionStorage.removeItem(this.storageKey); } } catch { /* Optional local retry retention. */ }
  }
  private async write(request: SavedRequest): Promise<void> {
    if (this.busy || !this.auth.state?.user) return;
    const generation = this.generation;
    const notice = element(objectWrite(request) ? 'object-save-status' : 'save-status');
    this.busy = true; this.retain(request); notice.textContent = 'Saving…'; this.render();
    try {
      const response = await this.auth.post(request.path, request.body);
      const body: unknown = await response.json(); const parsed = resultSchema.safeParse(body);
      if (generation !== this.generation) return;
      if (!parsed.success) {
        const code = typeof body === 'object' && body !== null && 'error' in body ? String(body.error) : 'unknown outcome';
        notice.textContent = `${code.replaceAll('_', ' ')} — retry the same request if uncertain.`; return;
      }
      this.retain(null);
      notice.textContent = parsed.data.receipt.status === 'applied' ? `${parsed.data.replayed ? 'Prior write confirmed' : 'Saved'} · revision ${parsed.data.receipt.revision}` : `Not saved: ${parsed.data.receipt.status.replaceAll('_', ' ')}`;
      await this.refreshRooms();
      if (request.path === '/api/rooms' && parsed.data.receipt.room && parsed.data.receipt.status === 'applied') {
        this.rooms.value = parsed.data.receipt.room.id; this.enter(parsed.data.receipt.room.id);
      }
    } catch { if (generation === this.generation) notice.textContent = 'Outcome unknown — retry last write with its original request ID.'; }
    finally { if (generation === this.generation) { this.busy = false; this.render(); } }
  }
  render(importing = false): void {
    const content = this.connection.state.content;
    const signature = content ? `${content.room.id}:${content.room.revision}:${content.objects.map(o => `${o.id}:${o.revision}`).join(',')}` : '';
    if (signature !== this.signature) {
      this.signature = signature; const selected = this.objects.value;
      this.objects.replaceChildren(...(content?.objects ?? []).map(o => new Option(`${o.value.name} · r${o.revision}`, o.id)));
      if (content?.objects.some(o => o.id === selected)) this.objects.value = selected;
      else if (this.objects.options.length) this.objects.selectedIndex = this.objects.options.length - 1;
      const option = [...this.rooms.options].find(o => o.value === content?.room.id); if (option && content) option.text = content.room.settings.name;
    }
    const user = this.auth.state?.user;
    const busy = this.busy || importing; this.rooms.disabled = importing;
    element('room-tools').hidden = !user;
    element<HTMLButtonElement>('enter-room').disabled = busy || !this.rooms.value;
    element<HTMLButtonElement>('create-room').disabled = busy || !user || !['owner', 'admin', 'builder'].includes(user.role);
    element<HTMLButtonElement>('rename-room').disabled = busy || !content || !this.connection.state.permissions.canManage;
    element<HTMLButtonElement>('add-fixture').disabled = this.busy || !content || !this.connection.state.permissions.canBuild;
    const selected = this.selected();
    for (const id of ['move-fixture', 'delete-fixture']) element<HTMLButtonElement>(id).disabled = this.busy || !selected || !!content?.room.environmentData?.objectIds.includes(selected.id) || !this.connection.state.permissions.canBuild || (!this.connection.state.permissions.canManage && selected.ownerId !== user?.id);
    element<HTMLButtonElement>('retry-write').disabled = this.busy || !this.pending || objectWrite(this.pending);
    element<HTMLButtonElement>('retry-object-write').disabled = this.busy || !this.pending || !objectWrite(this.pending);
  }
}
